import React, { useState, useCallback, useRef } from 'react';
import { Coins, Check, Upload, X, FileText, Image, Settings } from 'lucide-react';
import { CostSource, COST_SOURCE_LABELS, formatCostUnit } from '../../../../../../Helpers/maintenanceCostUtils';
import { CostUnit } from '../../../../../../types/Enums';
import { Attachment } from '../../../../../../types/attachments';
import { CostSettings, switchDropsCostData } from '../../../utils/costSettings';
import { tombstoneAttachmentDiff } from '../../../../../../api/fileDeletionApi';
import ConfirmationModal from '../../../../../../Components/ConfirmationModal';
import { validateFile, MAX_ATTACHMENTS_PER_ACTIVITY, MaintenanceAttachmentUploadResult } from '../../../utils/maintenanceAttachmentUtils';
import { getAuth } from 'firebase/auth';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { useDragDrop } from '../../../../../../hooks/useDragDrop';
import { UploadPickerModal } from '../../../../../../Components/UploadPickerModal';

export type CostConfigurationData = CostSettings;

const MAX_COST = 100000000;

interface CostConfigurationModalProps {
    title: string;
    costExplanation: string;
    /** Schablon cost: the template's unit price × quantity */
    calculatedCost: number;
    /** The template's unit price, in `costUnit` */
    templateUnitPrice: number;
    costUnit: CostUnit;
    /** What a unit price is multiplied by (m² / lm); null for kr/st */
    quantity: number | null;
    /** Tombstone entity type for abandoned uploads */
    attachmentEntityType: string;
    initialCostSource: CostSource;
    initialCustomCost?: number;
    initialCustomUnitPrice?: number;
    initialComment?: string;
    initialAttachments?: { [key: string]: Attachment };
    initialInvestmentPercentage?: number;
    uploadAttachment: (file: File, userId: string) => Promise<MaintenanceAttachmentUploadResult>;
    onSave: (data: CostConfigurationData) => void;
    onClose: () => void;
    onOpenEditTemplateCost: () => void;
    canEditTemplateCost?: boolean;
}

/**
 * Shared modal for configuring cost settings (both maintenance activities and replacements).
 * Handles cost source selection, custom costs, comments, attachments, and investment percentage.
 */
export const CostConfigurationModal: React.FC<CostConfigurationModalProps> = ({
    title,
    costExplanation,
    calculatedCost,
    templateUnitPrice,
    costUnit,
    quantity,
    attachmentEntityType,
    initialCostSource,
    initialCustomCost,
    initialCustomUnitPrice,
    initialComment,
    initialAttachments,
    initialInvestmentPercentage,
    uploadAttachment,
    onSave,
    onClose,
    onOpenEditTemplateCost,
    canEditTemplateCost = true
}) => {
    const [costSource, setCostSource] = useState<CostSource>(initialCostSource);
    const [customCost, setCustomCost] = useState<string>(
        initialCostSource === 'custom' && initialCustomCost !== undefined
            ? initialCustomCost.toString()
            : calculatedCost.toString()
    );
    const [customUnitPrice, setCustomUnitPrice] = useState<string>(
        (initialCostSource === 'customRate' && initialCustomUnitPrice !== undefined
            ? initialCustomUnitPrice
            : templateUnitPrice).toString()
    );
    const [comment, setComment] = useState<string>(initialComment || '');
    const [attachments, setAttachments] = useState<{ [key: string]: Attachment }>(
        initialAttachments || {}
    );
    const [investmentPercentage, setInvestmentPercentage] = useState<string>(
        (initialInvestmentPercentage ?? 0).toString()
    );
    const [saving, setSaving] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [pendingSave, setPendingSave] = useState<CostSettings | null>(null);
    // Uploads made in this modal: their only reference is the form until it is saved
    const sessionUploads = useRef<Set<string>>(new Set());

    const unitLabel = formatCostUnit(costUnit);
    const parsedUnitPrice = parseFloat(customUnitPrice);
    const customRateTotal = isNaN(parsedUnitPrice)
        ? null
        : Math.round(quantity === null ? parsedUnitPrice : parsedUnitPrice * quantity);

    /** Tombstones this session's uploads that `kept` does not hold */
    const tombstoneAbandonedUploads = useCallback((kept: { [key: string]: Attachment }): void => {
        const abandoned: { [key: string]: Attachment } = {};
        sessionUploads.current.forEach(key => {
            if (attachments[key] && !kept[key]) abandoned[key] = attachments[key];
        });
        void tombstoneAttachmentDiff(attachmentEntityType, abandoned, undefined);
    }, [attachments, attachmentEntityType]);

    const handleCancel = (): void => {
        tombstoneAbandonedUploads({});
        onClose();
    };

    // Handle selected/dropped files
    const handleFiles = useCallback(async (files: File[]) => {
        const file = files[0];
        if (!file) return;

        // Validate file
        const validation = validateFile(file);
        if (!validation.valid) {
            setError(validation.error || 'Ogiltig fil');
            return;
        }

        // Check attachment limit
        if (Object.keys(attachments).length >= MAX_ATTACHMENTS_PER_ACTIVITY) {
            setError(`Max ${MAX_ATTACHMENTS_PER_ACTIVITY} bilagor`);
            return;
        }

        const user = getAuth().currentUser;
        if (!user) {
            setError('Du måste vara inloggad för att ladda upp filer');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const result = await uploadAttachment(file, user.uid);

            if (result.success && result.attachment) {
                sessionUploads.current.add(result.attachment.key);
                setAttachments(prev => ({
                    ...prev,
                    [result.attachment!.key]: result.attachment!
                }));
            } else {
                setError(result.error || 'Ett fel uppstod vid uppladdning');
            }
        } catch (err) {
            console.error('Error uploading attachment:', err);
            setError('Ett fel uppstod vid uppladdning');
        } finally {
            setUploading(false);
        }
    }, [attachments, uploadAttachment]);

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });

    const save = (settings: CostSettings): void => {
        setSaving(true);
        try {
            tombstoneAbandonedUploads(settings.attachments);
            onSave(settings);
            onClose();
        } catch (err) {
            console.error('Error saving cost settings:', err);
            setError('Ett fel uppstod vid sparande. Försök igen.');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        let cost: number | undefined;
        let unitPrice: number | undefined;
        if (costSource === 'custom') {
            cost = parseFloat(customCost);
            if (isNaN(cost) || cost < 0) {
                setError('Kostnad får inte vara negativ');
                return;
            }
            if (cost > MAX_COST) {
                setError('Kostnad är för hög');
                return;
            }
        } else if (costSource === 'customRate') {
            unitPrice = parseFloat(customUnitPrice);
            if (isNaN(unitPrice) || unitPrice < 0) {
                setError('À-priset får inte vara negativt');
                return;
            }
            if ((customRateTotal ?? 0) > MAX_COST) {
                setError('Kostnad är för hög');
                return;
            }
        }

        // Validate investment percentage
        const percentage = parseInt(investmentPercentage) || 0;
        if (percentage < 0 || percentage > 100) {
            setError('Investeringsandel måste vara mellan 0 och 100%');
            return;
        }

        const isTemplate = costSource === 'template';
        const settings: CostSettings = {
            costSource,
            customCost: cost,
            customUnitPrice: unitPrice,
            comment: isTemplate ? '' : comment,
            attachments: isTemplate ? {} : attachments,
            investmentPercentage: percentage
        };

        if (switchDropsCostData(costSource, { comment, attachments })) {
            setPendingSave(settings);
            return;
        }
        save(settings);
    };

    const droppedSummary = (): string => {
        const parts: string[] = [];
        if (comment.trim()) parts.push('kommentaren');
        const count = Object.keys(attachments).length;
        if (count === 1) parts.push('1 bilaga');
        if (count > 1) parts.push(`${count} bilagor`);
        return parts.join(' och ');
    };

    const handleRemoveAttachment = (attachmentKey: string) => {
        // An upload from this session has no other reference: tombstone it now.
        // A saved attachment is tombstoned on save, if nothing else holds it.
        if (sessionUploads.current.has(attachmentKey) && attachments[attachmentKey]) {
            void tombstoneAttachmentDiff(attachmentEntityType, { [attachmentKey]: attachments[attachmentKey] }, undefined);
            sessionUploads.current.delete(attachmentKey);
        }
        setAttachments(prev => {
            const updated = { ...prev };
            delete updated[attachmentKey];
            return updated;
        });
    };

    const handleOpenAttachment = async (attachment: Attachment) => {
        try {
            const storage = getStorage();
            const fileRef = storageRef(storage, attachment.storagePath);
            const url = await getDownloadURL(fileRef);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error opening attachment:', err);
            setError('Kunde inte öppna bilaga');
        }
    };

    const getAttachmentIcon = (attachment: Attachment) => {
        if (attachment.attachmentType === 'image') {
            return <Image size={16} className="me-2" />;
        }
        return <FileText size={16} className="me-2" />;
    };

    const getAttachmentName = (attachment: Attachment): string => {
        if ('fileName' in attachment && attachment.fileName) {
            return attachment.fileName;
        }
        return 'Bilaga';
    };

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        handleCancel();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <Coins size={20} className="me-2" />
                                {title}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleCancel}
                                aria-label="Stäng"
                                disabled={saving || uploading}
                            />
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                )}

                                {/* Cost Explanation */}
                                <div className="alert alert-info mb-4">
                                    <small><strong>Beräkning:</strong> {costExplanation}</small>
                                </div>

                                {/* Cost Source Selection */}
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Kostnadsunderlag</label>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="costSource"
                                            id="costSourceTemplate"
                                            value="template"
                                            checked={costSource === 'template'}
                                            onChange={() => setCostSource('template')}
                                            disabled={saving}
                                        />
                                        <label className="form-check-label" htmlFor="costSourceTemplate">
                                            {COST_SOURCE_LABELS.template} <span className="text-muted">(mallens à-pris)</span>
                                        </label>
                                        {canEditTemplateCost && (
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 ms-2"
                                                onClick={onOpenEditTemplateCost}
                                                disabled={saving}
                                                style={{ fontSize: '0.875rem' }}
                                            >
                                                <Settings size={14} className="me-1" />
                                                Ändra mallkostnad
                                            </button>
                                        )}
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="costSource"
                                            id="costSourceCustomRate"
                                            value="customRate"
                                            checked={costSource === 'customRate'}
                                            onChange={() => setCostSource('customRate')}
                                            disabled={saving}
                                        />
                                        <label className="form-check-label" htmlFor="costSourceCustomRate">
                                            {COST_SOURCE_LABELS.customRate} <span className="text-muted">(eget à-pris för den här komponenten)</span>
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="costSource"
                                            id="costSourceCustom"
                                            value="custom"
                                            checked={costSource === 'custom'}
                                            onChange={() => setCostSource('custom')}
                                            disabled={saving}
                                        />
                                        <label className="form-check-label" htmlFor="costSourceCustom">
                                            {COST_SOURCE_LABELS.custom} <span className="text-muted">(verifierad kostnad)</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Cost Display/Input */}
                                {costSource === 'template' && (
                                    <div className="mb-3">
                                        <label className="form-label">Kostnad (kr)</label>
                                        <div className="form-control bg-light" style={{ cursor: 'default' }}>
                                            {calculatedCost.toLocaleString('sv-SE')} kr
                                            <span className="text-muted ms-2">(beräknad)</span>
                                        </div>
                                    </div>
                                )}
                                {costSource === 'customRate' && (
                                    <div className="mb-3">
                                        <label htmlFor="customUnitPrice" className="form-label">
                                            À-pris ({unitLabel}) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="customUnitPrice"
                                            value={customUnitPrice}
                                            onChange={(e) => setCustomUnitPrice(e.target.value)}
                                            min="0"
                                            step="any"
                                            required
                                            disabled={saving}
                                            style={{ maxWidth: '240px' }}
                                        />
                                        <small className="text-muted d-block mt-1">
                                            Mallens à-pris: {templateUnitPrice.toLocaleString('sv-SE')} {unitLabel}
                                        </small>
                                        <div className="mt-2" data-testid="custom-rate-total">
                                            Kostnad:{' '}
                                            <strong>
                                                {customRateTotal === null
                                                    ? '–'
                                                    : `${customRateTotal.toLocaleString('sv-SE')} kr`}
                                            </strong>
                                            {quantity !== null && (
                                                <span className="text-muted ms-2">
                                                    ({customUnitPrice || 0} {unitLabel} × {quantity.toLocaleString('sv-SE')} {unitLabel.replace('kr/', '')})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {costSource === 'custom' && (
                                    <div className="mb-3">
                                        <label htmlFor="costValue" className="form-label">
                                            Kostnad enligt offert (kr) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="costValue"
                                            value={customCost}
                                            onChange={(e) => setCustomCost(e.target.value)}
                                            min="0"
                                            step="1"
                                            required
                                            disabled={saving}
                                        />
                                    </div>
                                )}

                                {/* Comment (offert and anpassad schablon) */}
                                {costSource !== 'template' && (
                                    <div className="mb-3">
                                        <label htmlFor="costComment" className="form-label">
                                            Kommentar (valfritt)
                                        </label>
                                        <textarea
                                            className="form-control"
                                            id="costComment"
                                            rows={3}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            disabled={saving}
                                            placeholder={costSource === 'custom'
                                                ? 'T.ex. leverantör, offertdatum, vad som ingår...'
                                                : 'Förklara varför à-priset skiljer sig från mallen...'}
                                        />
                                    </div>
                                )}

                                {/* Attachments (offert and anpassad schablon) */}
                                {costSource !== 'template' && (
                                    <div
                                        className="mb-3"
                                        {...dragHandlers}
                                        style={{
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: isDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                                            backgroundColor: isDragOver ? '#f0f8ff' : 'transparent',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <label className="form-label">
                                            {costSource === 'custom' ? 'Offert och andra bilagor (valfritt)' : 'Bilagor (valfritt)'}
                                        </label>

                                        {/* Attachment list */}
                                        {Object.keys(attachments).length > 0 && (
                                            <div className="mb-2">
                                                {Object.values(attachments).map((attachment) => (
                                                    <div
                                                        key={attachment.key}
                                                        className="d-flex align-items-center justify-content-between p-2 bg-light rounded mb-1"
                                                    >
                                                        <button
                                                            type="button"
                                                            className="btn btn-link text-decoration-none p-0 text-start"
                                                            onClick={() => handleOpenAttachment(attachment)}
                                                        >
                                                            {getAttachmentIcon(attachment)}
                                                            {getAttachmentName(attachment)}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemoveAttachment(attachment.key)}
                                                            disabled={saving || uploading}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Upload button */}
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setShowUploadModal(true)}
                                            disabled={saving || uploading || Object.keys(attachments).length >= MAX_ATTACHMENTS_PER_ACTIVITY}
                                        >
                                            {uploading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Laddar upp...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload size={14} className="me-2" />
                                                    Lägg till bilaga
                                                </>
                                            )}
                                        </button>
                                        <small className="text-muted d-block mt-1">
                                            Max {MAX_ATTACHMENTS_PER_ACTIVITY} bilagor. Tillåtna format: bilder och PDF. Du kan också dra och släppa filer här.
                                        </small>
                                    </div>
                                )}

                                {/* Investment Percentage */}
                                <div className="mb-3">
                                    <label htmlFor="investmentPercentage" className="form-label">
                                        Investeringsandel (%)
                                    </label>
                                    <div className="input-group" style={{ maxWidth: '200px' }}>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="investmentPercentage"
                                            value={investmentPercentage}
                                            onChange={(e) => setInvestmentPercentage(e.target.value)}
                                            min="0"
                                            max="100"
                                            step="1"
                                            disabled={saving}
                                        />
                                        <span className="input-group-text">%</span>
                                    </div>
                                    <small className="text-muted">
                                        Andel av kostnaden som bokförs som investering (0% = allt är kostnad)
                                    </small>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCancel}
                                        disabled={saving || uploading}
                                    >
                                        Avbryt
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || uploading}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Sparar...
                                            </>
                                        ) : (
                                            <>
                                                <Check size={16} className="me-2" />
                                                Spara
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>

            {/* Upload picker (drag-drop, paste, or pick from device) */}
            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                accept="image/*,application/pdf"
                multiple={false}
                title="Ladda upp"
            />

            <ConfirmationModal
                show={pendingSave !== null}
                title="Byta till schablon?"
                message={`Med schablon tas ${droppedSummary()} bort från kostnaden.`}
                confirmText="Byt och ta bort"
                cancelText="Avbryt"
                variant="warning"
                onConfirm={() => {
                    const settings = pendingSave;
                    setPendingSave(null);
                    if (settings) save(settings);
                }}
                onCancel={() => setPendingSave(null)}
            />
        </>
    );
};
